/**
 * Booking lead time ("Vorlaufzeit"): a per-listing number of calendar days at the front of the
 * calendar that cannot be booked, so the provider has time to clarify a request before its start
 * date arrives.
 *
 * Stored as `publicData.bookingLeadDays`. Sharetribe has no concept of a lead time — not in the
 * availability plan, not in the process EDN — so the rule lives entirely in this template, on two
 * levels that have to agree:
 *
 *   1. the three booking forms raise the calendar's lower bound (OrderPanel -> BookingDatesForm /
 *      BookingTimeForm / BookingFixedDurationForm), and
 *   2. the server endpoints that create a booking refuse a start date below it
 *      (server/api-util/bookingLeadTime.js).
 *
 * Level 2 is not belt-and-braces: the calendar is the only thing level 1 controls, and a booking
 * request can reach the server without ever passing through it — a selection restored from session
 * storage after a sign-in, the iOS app, the MCP connector. The Marketplace API itself would happily
 * accept those, since availability exists.
 *
 * This module is the ONLY place `publicData.bookingLeadDays` is read on the client — the same
 * discipline as `safeExternalBookingUrl` in OrderPanel. Everything downstream asks for an already
 * resolved number, which is why a garbage value can only ever mean "no lead time" instead of
 * leaking into date arithmetic somewhere far from here.
 */
import { getStartOf } from './dates';

/**
 * Upper bound for a lead time, in days.
 *
 * `dayCountAvailableForBooking` is 90 (src/config/configStripe.js), so a lead time anywhere near it
 * leaves barely any bookable days at all. Clamping instead of trusting the stored number is what
 * keeps a typo ("300" for "30", or a bad import) from silently killing a listing's calendar: the
 * listing then has a long lead time rather than no calendar.
 */
export const MAX_BOOKING_LEAD_DAYS = 30;

/**
 * Read the configured lead time off a listing's public data.
 *
 * Anything that is not a positive whole number of days — missing, null, "", "abc", -1, 2.5 — means
 * 0, i.e. exactly the behaviour every listing had before this field existed. That is the whole
 * backwards-compatibility contract in one line.
 *
 * @param {Object} [publicData] listing.attributes.publicData
 * @returns {number} whole days of lead time, 0..MAX_BOOKING_LEAD_DAYS
 */
export const resolveBookingLeadDays = publicData => {
  const raw = publicData?.bookingLeadDays;
  // Number and numeric string both occur in the wild: the wizard writes a number, while Integration
  // API imports and a hosted "long" listing field can deliver "3".
  //
  // Strings werden mit /^\d+$/ geprüft, nicht bloss an Number() gereicht: Number akzeptiert auch
  // "0x1e" (=30), "1e1" (=10) und " 3 " — alles Formen, die Number.isInteger anschliessend durchwinkt.
  // Der String-Pfad existiert genau für Importe, und ein Import, der "1e1" schreibt, wollte nie zehn
  // Tage Vorlauf konfigurieren.
  const parsed =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && /^\d+$/.test(raw.trim())
      ? Number(raw)
      : NaN;

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return 0;
  }
  return Math.min(parsed, MAX_BOOKING_LEAD_DAYS);
};

/**
 * The earliest moment a booking may start: the start of a day in the LISTING's time zone, not the
 * visitor's. A Swiss space and a visitor in New York have to see the same floor.
 *
 * With `leadDays` 0 this is the start of today — literally what the three forms computed before this
 * module existed (`getStartOf(TODAY, 'day', timeZone)`), because getStartOf's offset branch is a
 * no-op at 0. That identity is what makes a listing without the field behave exactly as before.
 *
 * @param {number} leadDays resolved lead days (see resolveBookingLeadDays)
 * @param {string} timeZone IANA time zone key of the listing
 * @param {Date} [now] current time (injectable for tests)
 * @returns {Date} start of the earliest bookable day
 */
export const getEarliestBookableDate = (leadDays, timeZone, now = new Date()) =>
  getStartOf(now, 'day', timeZone, leadDays, 'days');

/**
 * Wie das erste buchbare Datum geschrieben wird — an EINER Stelle.
 *
 * Drei Oberflächen nennen dasselbe Datum: der Hinweis im Buchungsformular, die Fehlermeldung, wenn
 * doch ein zu früher Tag beim Server ankommt, und die Vorschau im Anbieter-Dialog. Schreiben sie es
 * unterschiedlich, widerspricht die Fehlermeldung sichtbar dem Hinweis zwei Zeilen darüber.
 *
 * `timeZone` ist nicht optional-im-Sinne-von-egal: das Datum ist eine Mitternacht in der Zone des
 * LISTINGS. Ohne die Angabe formatiert intl in der Zone des Besuchers, und wer westlich davon sitzt,
 * liest den Vortag.
 *
 * @param {Object} intl react-intl
 * @param {Date} date erstes buchbares Datum (siehe getEarliestBookableDate)
 * @param {string} timeZone IANA-Zeitzone des Listings
 * @returns {string}
 */
export const formatEarliestBookableDate = (intl, date, timeZone) =>
  intl.formatDate(date, { weekday: 'short', day: 'numeric', month: 'long', timeZone });
